// src/pages/sow-template/useSowTemplate.js
//
// All state, derived values, and handlers for the SOW Template builder.
// Split out of SowTemplatePage.jsx so the page is composition/markup only.
//
// Backed by the real backend (presentation/sow/sow_template_controller.py)
// via sowTemplateApi (../../api/client.js). Scope/permission plumbing
// mirrors ../rules/useRulesConfig.js's shape — same 3-tier (global/org/
// account) RBAC model, same useAuth()/useWorkspace() sources.

import { useEffect, useMemo, useRef, useState } from "react";
import { getApiError, sowTemplateApi } from "../../api/client.js";
import { downloadBlob } from "../../api/sowApi";
import { useAuth, normalizePerms } from "../../context/AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import {
  extractPlaceholdersFromText,
  renderPreviewHTML,
  safeKeyFromTitle,
} from "./sowTemplateHelpers";
import { emptyTemplate } from "./sowTemplateOptions";

export function useSowTemplate() {
  const { hasPermissionAnywhere, hasGlobalPermission, permissionsMap, permissionsLoading } = useAuth();
  const { accounts } = useWorkspace();

  const canManageGlobal = hasGlobalPermission("sow:templates:manage_global");
  // An org-wide grant lands in global_permissions, same as any other
  // org-wide role — see AuthContext's own note on why
  // hasPermission()/hasAnyPermission() check global first.
  const canManageOrgWide = hasGlobalPermission("sow:templates:manage");
  const manageableAccountIds = useMemo(
    () =>
      Object.entries(permissionsMap.accounts || {})
        .filter(([, perms]) => normalizePerms(perms).includes("sow:templates:manage"))
        .map(([id]) => Number(id)),
    [permissionsMap]
  );
  const canCreateAnything = canManageGlobal || canManageOrgWide || manageableAccountIds.length > 0;
  const selectableAccounts = useMemo(() => {
    if (canManageOrgWide) return accounts;
    return accounts.filter((a) => manageableAccountIds.includes(a.account_id));
  }, [accounts, canManageOrgWide, manageableAccountIds]);

  const canManageTemplate = (tpl) => {
    if (!tpl) return false;
    if (tpl.org_id == null) return canManageGlobal;
    if (tpl.account_id == null) return canManageOrgWide;
    return canManageOrgWide || manageableAccountIds.includes(tpl.account_id);
  };

  const [q, setQ] = useState("");
  const [contractType, setContractType] = useState("");
  const [scopeFilter, setScopeFilter] = useState(""); // "" | "global" | "org" | "account"
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [tpl, setTpl] = useState(null);
  const [selectedSectionKey, setSelectedSectionKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [addSectionModalOpen, setAddSectionModalOpen] = useState(false);

  const [scope, setScope] = useState(() =>
    canManageGlobal ? "global" : canManageOrgWide ? "org" : "account"
  );
  const [scopeAccountId, setScopeAccountId] = useState("");

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadVersion, setUploadVersion] = useState("1.0");
  const [uploadContractType, setUploadContractType] = useState("");
  const [docxFile, setDocxFile] = useState(null);

  const [placeholderModalOpen, setPlaceholderModalOpen] = useState(false);
  const [phEditKey, setPhEditKey] = useState("");
  const [phKey, setPhKey] = useState("");
  const [phLabel, setPhLabel] = useState("");
  const [phType, setPhType] = useState("string");
  const [phRequired, setPhRequired] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);

  const editorRef = useRef(null);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (type === "success") setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const refreshList = async () => {
    setBusy(true);
    try {
      const res = await sowTemplateApi.list(q, contractType, scopeFilter);
      setRows(res?.items || []);
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => refreshList(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, contractType, scopeFilter]);

  const loadTemplate = async (id) => {
    setBusy(true);
    try {
      const full = await sowTemplateApi.get(id);

      const tags = full.tags_schema_json && typeof full.tags_schema_json === "object" ? full.tags_schema_json : {};
      const sections = Array.isArray(full.sections_json) ? full.sections_json : [];

      const normalizedSections = sections
        .map((s, i) => ({
          key: (s.key || `section_${i + 1}`).trim(),
          title: (s.title || `Section ${i + 1}`).trim(),
          content_template: s.content_template || "",
          prompt_hint: s.prompt_hint || "",
          order: Number.isFinite(Number(s.order)) ? Number(s.order) : i,
        }))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((s, idx) => ({ ...s, order: idx }));

      setTpl({
        id: full.id,
        name: full.name || "",
        version: full.version || "1.0",
        contract_type: full.contract_type || "",
        active: full.active !== false,
        tags_schema_json: tags,
        sections_json: normalizedSections,
        has_docx: !!full.has_docx,
        org_id: full.org_id ?? null,
        account_id: full.account_id ?? null,
        scope: full.scope || "global",
      });
      setSelectedSectionKey(normalizedSections[0]?.key || "");
      setSelectedId(id);
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startNewTemplate = () => {
    setTpl({ ...emptyTemplate });
    setSelectedId(null);
    setSelectedSectionKey("");
    setScope(canManageGlobal ? "global" : canManageOrgWide ? "org" : "account");
    setScopeAccountId("");
  };

  const selectedSection = useMemo(() => {
    if (!tpl?.sections_json?.length) return null;
    return tpl.sections_json.find((s) => s.key === selectedSectionKey) || tpl.sections_json[0];
  }, [tpl, selectedSectionKey]);

  const allPlaceholders = useMemo(() => {
    const schemaKeys = Object.keys(tpl?.tags_schema_json || {});
    const used = new Set();
    for (const s of tpl?.sections_json || []) {
      extractPlaceholdersFromText(s.content_template).forEach((k) => used.add(k));
      extractPlaceholdersFromText(s.title).forEach((k) => used.add(k));
    }
    return Array.from(new Set([...schemaKeys, ...used])).sort();
  }, [tpl]);

  const unregisteredUsedPlaceholders = useMemo(() => {
    const schema = tpl?.tags_schema_json || {};
    const used = new Set();
    for (const s of tpl?.sections_json || []) {
      extractPlaceholdersFromText(s.content_template).forEach((k) => used.add(k));
      extractPlaceholdersFromText(s.title).forEach((k) => used.add(k));
    }
    return Array.from(used)
      .filter((k) => !schema[k])
      .sort();
  }, [tpl]);

  const setTemplateField = (field, value) => setTpl((prev) => ({ ...prev, [field]: value }));

  const setSectionField = (sectionKey, patch) => {
    if (!tpl) return;
    setTpl((prev) => ({
      ...prev,
      sections_json: (prev.sections_json || []).map((s) => (s.key === sectionKey ? { ...s, ...patch } : s)),
    }));
  };

  const addSection = (title) => {
    if (!title) return;
    setTpl((prev) => {
      const base = prev || { ...emptyTemplate };
      const key0 = safeKeyFromTitle(title);
      const exists = new Set((base.sections_json || []).map((s) => s.key));
      let key = key0;
      let n = 2;
      while (exists.has(key)) key = `${key0}_${n++}`;

      const next = [...(base.sections_json || [])];
      next.push({ key, title, content_template: "", prompt_hint: "", order: next.length });
      setSelectedSectionKey(key);
      return { ...base, sections_json: next };
    });
    setAddSectionModalOpen(false);
    if (!tpl) {
      setSelectedId(null);
    }
  };

  const deleteSection = (sectionKey) => {
    if (!tpl) return;
    const next = (tpl.sections_json || []).filter((s) => s.key !== sectionKey).map((s, i) => ({ ...s, order: i }));
    setTpl({ ...tpl, sections_json: next });
    setSelectedSectionKey(next[0]?.key || "");
  };

  const moveSection = (sectionKey, dir) => {
    if (!tpl) return;
    const arr = [...(tpl.sections_json || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const i = arr.findIndex((s) => s.key === sectionKey);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setTpl({ ...tpl, sections_json: arr.map((s, idx) => ({ ...s, order: idx })) });
  };

  const openAddPlaceholder = (prefillKey = "") => {
    setPhEditKey("");
    setPhKey(prefillKey || "");
    setPhLabel(prefillKey ? prefillKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");
    setPhType("string");
    setPhRequired(false);
    setPlaceholderModalOpen(true);
  };

  const openEditPlaceholder = (phK) => {
    const meta = (tpl?.tags_schema_json || {})[phK] || {};
    setPhEditKey(phK);
    setPhKey(phK);
    setPhLabel(meta.label || phK);
    setPhType(meta.type || "string");
    setPhRequired(!!meta.required);
    setPlaceholderModalOpen(true);
  };

  const savePlaceholder = () => {
    if (!tpl) return;
    const k = String(phKey || "").trim().toLowerCase().replace(/\s+/g, "_");
    if (!k) return showMsg("error", "Placeholder key is required.");
    if (!/^[a-zA-Z0-9_]+$/.test(k)) {
      return showMsg("error", "Placeholder key must be letters/numbers/underscore only.");
    }

    const nextSchema = { ...(tpl.tags_schema_json || {}) };
    nextSchema[k] = { type: phType || "string", label: phLabel || k, required: !!phRequired };

    if (phEditKey && phEditKey !== k) {
      delete nextSchema[phEditKey];
      const nextSections = (tpl.sections_json || []).map((s) => ({
        ...s,
        content_template: String(s.content_template || "")
          .replaceAll(`{{${phEditKey}}}`, `<${phLabel || k}>`)
          .replaceAll(`<${phEditKey}>`, `<${phLabel || k}>`),
      }));
      setTpl({ ...tpl, tags_schema_json: nextSchema, sections_json: nextSections });
    } else {
      setTpl({ ...tpl, tags_schema_json: nextSchema });
    }

    setPlaceholderModalOpen(false);
  };

  const deletePlaceholder = (phK) => {
    if (!tpl) return;
    const nextSchema = { ...(tpl.tags_schema_json || {}) };
    delete nextSchema[phK];
    setTpl({ ...tpl, tags_schema_json: nextSchema });
  };

  const insertPlaceholder = (phK) => {
    if (!tpl) return;
    const meta = (tpl.tags_schema_json || {})[phK] || {};
    const label = meta.label || phK.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const token = `<${label}>`;

    const currentKey = selectedSection?.key || tpl.sections_json?.[0]?.key;
    if (!currentKey) return showMsg("error", "No section exists. Please add a section first.");

    const section = (tpl.sections_json || []).find((s) => s.key === currentKey);
    const cur = section?.content_template || "";

    const el = editorRef.current;
    if (!el) {
      setSectionField(currentKey, { content_template: cur + (cur ? "\n" : "") + token });
      return;
    }

    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? start;
    const next = cur.slice(0, start) + token + cur.slice(end);
    setSectionField(currentKey, { content_template: next });

    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const saveTemplate = async () => {
    if (!tpl) return;
    if (!tpl.id && scope === "account" && !scopeAccountId) {
      return showMsg("error", "Choose an account for this template's scope.");
    }
    setBusy(true);
    try {
      const body = {
        name: tpl.name,
        contract_type: tpl.contract_type || null,
        version: tpl.version || "1.0",
        active: tpl.active !== false,
        tags_schema_json: tpl.tags_schema_json || {},
        sections_json: (tpl.sections_json || []).map((s) => ({
          key: s.key,
          title: s.title,
          content_template: s.content_template || "",
          prompt_hint: s.prompt_hint || "",
          order: s.order ?? 0,
        })),
      };

      // Scope is set once at creation and never changes on update.
      if (!tpl.id) {
        body.is_global = scope === "global";
        body.account_id = scope === "account" && scopeAccountId ? Number(scopeAccountId) : null;
      }

      const saved = tpl.id ? await sowTemplateApi.update(tpl.id, body) : await sowTemplateApi.create(body);

      showMsg("success", "Template saved.");
      await refreshList();
      await loadTemplate(saved.id);
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteTemplate = async () => {
    if (!tpl?.id) return;
    if (!window.confirm("Delete this template?")) return;
    setBusy(true);
    try {
      await sowTemplateApi.delete(tpl.id);
      setTpl(null);
      setSelectedId(null);
      setSelectedSectionKey("");
      await refreshList();
      showMsg("success", "Template deleted.");
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadDocx = async () => {
    if (!docxFile) return;
    const name = (uploadName || "").trim();
    if (!name) return showMsg("error", "Please enter a template name for the upload.");
    if (scope === "account" && !scopeAccountId) {
      return showMsg("error", "Choose an account for this template's scope.");
    }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("version", uploadVersion || "1.0");
      if ((uploadContractType || "").trim()) fd.append("contract_type", uploadContractType.trim());
      fd.append("active", "true");
      fd.append("is_global", scope === "global" ? "true" : "false");
      if (scope === "account" && scopeAccountId) fd.append("account_id", String(scopeAccountId));
      fd.append("file", docxFile);

      const saved = await sowTemplateApi.uploadDocx(fd);

      showMsg("success", "Uploaded & parsed DOCX.");
      setDocxFile(null);
      setUploadName("");
      setUploadModalOpen(false);
      await refreshList();
      await loadTemplate(saved.id);
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplateDocx = async () => {
    if (!tpl?.id) return;

    setBusy(true);
    try {
      const blob = await sowTemplateApi.downloadDocx(tpl.id);
      const safeName = String(tpl.name || "template").replace(/[/\\:*?"<>|]/g, "_");
      downloadBlob(blob, `${safeName}_v${tpl.version || "1.0"}.docx`);
    } catch (e) {
      showMsg("error", getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const previewHtml = useMemo(() => renderPreviewHTML(tpl), [tpl]);

  return {
    // permissions / scope capability
    canManageGlobal,
    canManageOrgWide,
    manageableAccountIds,
    canCreateAnything,
    permissionsLoading,
    selectableAccounts,
    canManageTemplate,
    canViewPage: hasPermissionAnywhere(["sow:templates:view", "sow:templates:manage"]),

    // list/search
    q, setQ, contractType, setContractType, scopeFilter, setScopeFilter,
    rows, selectedId, refreshList,
    loadTemplate, startNewTemplate,

    // template + editing
    tpl, setTemplateField, saveTemplate, deleteTemplate, busy, msg,
    scope, setScope, scopeAccountId, setScopeAccountId,

    // sections
    selectedSectionKey, setSelectedSectionKey, selectedSection,
    addSection, deleteSection, moveSection, setSectionField, editorRef,
    addSectionModalOpen, setAddSectionModalOpen,

    // placeholders
    allPlaceholders, unregisteredUsedPlaceholders,
    placeholderModalOpen, setPlaceholderModalOpen,
    phEditKey, phKey, setPhKey, phLabel, setPhLabel, phType, setPhType, phRequired, setPhRequired,
    openAddPlaceholder, openEditPlaceholder, savePlaceholder, deletePlaceholder, insertPlaceholder,

    // upload
    uploadModalOpen, setUploadModalOpen,
    uploadName, setUploadName, uploadVersion, setUploadVersion, uploadContractType, setUploadContractType,
    docxFile, setDocxFile, uploadDocx,

    // preview / download
    previewOpen, setPreviewOpen, previewHtml, downloadTemplateDocx,
  };
}
